import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, Activity, Ticket, Server, Boxes, Network, ShieldCheck, ShieldAlert,
  FileText, KeyRound, FileSignature, Cpu, HardDriveDownload, BarChart3, Sparkles,
  Building2, Users, Repeat, TerminalSquare, Search, Bell, ChevronDown, LogOut, Wand2, GanttChart
} from "lucide-react";
import { useState } from "react";
import { canAccess } from "@/lib/permissions";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";

const NAV = [
  {
    group: "End User",
    items: [
      { to: "/employee", label: "My Support Portal", icon: Ticket, module: "employee", testid: "nav-employee" },
    ],
  },
  {
    group: "Client Management",
    items: [
      { to: "/clients", label: "Companies", icon: Building2, module: "clients", testid: "nav-clients" },
      { to: "/users", label: "Users", icon: Users, module: "users", testid: "nav-users" },
    ],
  },
  {
    group: "Command Center",
    items: [
      { to: "/", label: "Executive", icon: LayoutDashboard, module: "dashboard", testid: "nav-executive" },
      { to: "/rmm", label: "RMM Operations", icon: Activity, module: "rmm", testid: "nav-rmm" },
      { to: "/recurring", label: "Recurring Issues", icon: Repeat, module: "recurring", testid: "nav-recurring" },
    ],
  },
  {
    group: "Service Desk",
    items: [
      { to: "/tickets", label: "Tickets", icon: Ticket, module: "tickets", testid: "nav-tickets" },
    ],
  },
  {
    group: "Assets & Inventory",
    items: [
      { to: "/assets", label: "Asset Lifecycle", icon: Server, module: "assets", testid: "nav-assets" },
      { to: "/inventory", label: "Inventory", icon: Boxes, module: "inventory", testid: "nav-inventory" },
      { to: "/inventory/endpoint-intelligence", label: "Endpoint Intelligence", icon: Cpu, module: "inventory", testid: "nav-endpoint-intel" },
      { to: "/network", label: "Network Discovery", icon: Network, module: "network", testid: "nav-network" },
    ],
  },
  {
    group: "Compliance & Security",
    items: [
      { to: "/compliance", label: "Compliance", icon: ShieldCheck, module: "compliance", testid: "nav-compliance" },
      { to: "/security", label: "Security Ops", icon: ShieldAlert, module: "security", testid: "nav-security" },
      { to: "/vulnerabilities", label: "Vulnerabilities", icon: Wand2, module: "vulnerabilities", testid: "nav-vulns" },
    ],
  },
  {
    group: "Knowledge",
    items: [
      { to: "/docs", label: "Documentation", icon: FileText, module: "docs", testid: "nav-docs" },
      { to: "/vault", label: "Credential Vault", icon: KeyRound, module: "vault", testid: "nav-vault" },
    ],
  },
  {
    group: "Governance",
    items: [
      { to: "/contracts", label: "Contracts", icon: FileSignature, module: "contracts", testid: "nav-contracts" },
      { to: "/licenses", label: "Licenses", icon: Cpu, module: "licenses", testid: "nav-licenses" },
      { to: "/backups", label: "Backups", icon: HardDriveDownload, module: "backups", testid: "nav-backups" },
    ],
  },
  {
    group: "Intelligence",
    items: [
      { to: "/reports", label: "Reports", icon: BarChart3, module: "reports", testid: "nav-reports" },
      { to: "/exports", label: "Export Engine", icon: BarChart3, module: "exports", testid: "nav-exports" },
      { to: "/customer-success", label: "Customer Success", icon: Users, module: "customer_success", testid: "nav-cs" },
      { to: "/customer-risk", label: "Customer Risk", icon: ShieldAlert, module: "customer_risk", testid: "nav-risk" },
      { to: "/leakage", label: "Revenue Leakage", icon: GanttChart, module: "leakage", testid: "nav-leak" },
      { to: "/opportunities", label: "Sales Opportunities", icon: GanttChart, module: "opportunities", testid: "nav-opps" },
      { to: "/msp-bi", label: "MSP BI", icon: GanttChart, module: "msp_bi", testid: "nav-msp-bi" },
      { to: "/storytelling", label: "Executive Storytelling", icon: Sparkles, module: "storytelling", testid: "nav-story" },
      { to: "/ai", label: "AI Operations", icon: Sparkles, module: "ai", testid: "nav-ai" },
    ],
  },
  {
    group: "Identity & Integrations",
    items: [
      { to: "/identity", label: "AD · Entra · M365", icon: Building2, module: "identity", testid: "nav-identity" },
      { to: "/reconciliation", label: "Reconciliation", icon: Repeat, module: "reconciliation", testid: "nav-recon" },
      { to: "/evidence", label: "Evidence Vault", icon: FileText, module: "evidence", testid: "nav-evidence" },
      { to: "/email", label: "Email Engine", icon: FileText, module: "email", testid: "nav-email" },
      { to: "/mfa", label: "MFA Settings", icon: ShieldCheck, module: "mfa", testid: "nav-mfa" },
    ],
  },
  {
    group: "Endpoint Architecture",
    items: [
      { to: "/agents", label: "APOCALYNX Agent", icon: TerminalSquare, module: "agents", testid: "nav-agents" },
      { to: "/remediation", label: "Auto Remediation", icon: Wand2, module: "remediation", testid: "nav-remediation" },
      { to: "/recurring-v2", label: "Recurring 2.0", icon: Repeat, module: "recurring_v2", testid: "nav-rv2" },
      { to: "/portal", label: "Client Portal Preview", icon: Building2, module: "portal", testid: "nav-portal" },
    ],
  },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [q, setQ] = useState("");

  const initials = (user?.name || "MS").split(" ").map(s => s[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div className="min-h-screen bg-[#09090B] text-zinc-100 flex">
      {/* Sidebar */}
      <aside className="w-64 flex-shrink-0 bg-[#0a0a0c] border-r border-zinc-900 fixed left-0 top-0 h-screen flex flex-col" data-testid="app-sidebar">
        <div className="px-5 h-14 flex items-center border-b border-zinc-900">
          <div className="w-8 h-8 rounded-md bg-gradient-to-br from-blue-500 to-sky-400 flex items-center justify-center font-mono font-bold text-white">A</div>
          <div className="ml-3">
            <div className="font-display font-semibold text-[15px] tracking-tight">APOCALYNX</div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 -mt-0.5">MSPOS · Command</div>
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto py-3 px-2">
          {NAV.map((g) => {
            const visibleItems = g.items.filter((it) => canAccess(user, it.module));
            if (visibleItems.length === 0) return null;
            return (
            <div key={g.group} className="mb-4">
              <div className="px-3 mb-1 text-[10px] uppercase tracking-[0.16em] text-zinc-500">{g.group}</div>
              {visibleItems.map((it) => {
                const active = loc.pathname === it.to || (it.to !== "/" && loc.pathname.startsWith(it.to));
                const Icon = it.icon;
                return (
                  <Link
                    to={it.to}
                    key={it.to}
                    data-testid={it.testid}
                    className={`flex items-center gap-3 px-3 py-1.5 mx-1 rounded-md text-[13px] transition-colors ${active ? "bg-blue-600/15 text-zinc-50 border border-blue-600/30" : "text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900/60 border border-transparent"}`}
                  >
                    <Icon size={15} />
                    <span>{it.label}</span>
                  </Link>
                );
              })}
            </div>
          )})}
        </nav>
        <div className="px-3 py-3 border-t border-zinc-900 text-[11px] text-zinc-500 font-mono">
          <div className="flex items-center justify-between">
            <span>v1.0.0</span>
            <span className="text-emerald-500">● online</span>
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 ml-64 min-h-screen">
        {/* Topbar */}
        <header className="h-14 sticky top-0 z-30 bg-[#09090B]/85 backdrop-blur-md border-b border-zinc-900 flex items-center px-6 gap-4">
          <div className="flex items-center gap-2 text-zinc-500 text-xs font-mono uppercase tracking-widest">
            <span>Tenant</span>
            <span className="text-zinc-200 normal-case">CoreBridge MSP</span>
            <ChevronDown size={12} />
          </div>
          <div className="flex-1 flex justify-center">
            <div className="w-full max-w-xl relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && q) nav(`/search?q=${encodeURIComponent(q)}`); }}
                placeholder="Search assets, tickets, clients, documents…"
                data-testid="global-search"
                className="w-full bg-zinc-900/70 border border-zinc-800 rounded-md pl-9 pr-3 py-1.5 text-sm placeholder:text-zinc-600 focus:outline-none focus:border-blue-500/60"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-mono text-zinc-600 border border-zinc-800 rounded px-1 py-0.5">⌘K</span>
            </div>
          </div>
          <button data-testid="topbar-notifications" className="p-1.5 hover:bg-zinc-900 rounded-md text-zinc-400 relative">
            <Bell size={16} />
            <span className="absolute top-1 right-1 w-1.5 h-1.5 bg-red-500 rounded-full" />
          </button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="user-menu" className="flex items-center gap-2 hover:bg-zinc-900 rounded-md px-2 py-1">
                <Avatar className="h-7 w-7"><AvatarFallback className="text-[10px] bg-blue-600/30 text-blue-300">{initials}</AvatarFallback></Avatar>
                <div className="text-left">
                  <div className="text-xs font-medium leading-tight">{user?.name || "Operator"}</div>
                  <div className="text-[10px] text-zinc-500 leading-tight uppercase tracking-widest">{user?.role?.replaceAll("_", " ")}</div>
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-zinc-950 border-zinc-800 text-zinc-200">
              <DropdownMenuItem className="text-xs">{user?.email}</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => { logout(); nav("/login"); }} data-testid="logout-btn">
                <LogOut size={14} className="mr-2" /> Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        <main className="p-6 fade-up">{children}</main>
      </div>
    </div>
  );
}
